import { CommonModule } from '@angular/common';
import { Component, forwardRef, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';

export interface GroupOption {
	id: string;
	name: string;
	description: string;
	icon: string;
}

@Component({
	selector: 'group-selector',
	standalone: true,
	imports: [
		CommonModule,
		MatCardModule,
		MatIconModule,
		MatTooltipModule
	],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => GroupSelectorComponent),
			multi: true
		}
	],
	templateUrl: './group-selector.component.html'
})
export class GroupSelectorComponent implements ControlValueAccessor {
	@Input() multiple = false;

	groups: GroupOption[] = [
		{
			id: 'user-admin',
			name: 'User Admin',
			description: 'User able to manage other users and their permissions',
			icon: 'heroicons_outline:user-group'
		},
		{
			id: 'exploration-users',
			name: 'Exploration',
			description: 'Access to geological log data and analysis tools',
			icon: 'heroicons_outline:globe'
		},
		{
			id: 'pmo-innovation',
			name: 'Innovation',
			description: 'Access to suggestions and innovation tools',
			icon: 'heroicons_outline:light-bulb'
		}
	];

	selectedGroups: string[] = [];

	private onChange = (value: string | string[]) => {};
	private onTouched = () => {};

	writeValue(value: string | string[]): void {
		if (value) {
			this.selectedGroups = Array.isArray(value) ? value : [value];
		} else {
			this.selectedGroups = [];
		}
	}

	registerOnChange(fn: (value: string | string[]) => void): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: () => void): void {
		this.onTouched = fn;
	}

	toggleGroup(groupId: string): void {
		this.onTouched();

		if (this.multiple) {
			const index = this.selectedGroups.indexOf(groupId);
			if (index > -1) {
				this.selectedGroups.splice(index, 1);
			} else {
				this.selectedGroups.push(groupId);
			}
			this.onChange(this.selectedGroups);
		} else {
			if (this.selectedGroups.includes(groupId)) {
				this.selectedGroups = [];
				this.onChange('');
			} else {
				this.selectedGroups = [groupId];
				this.onChange(groupId);
			}
		}
	}

	isSelected(groupId: string): boolean {
		return this.selectedGroups.includes(groupId);
	}
}
